import React, { useState } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { FaSave, FaTimesCircle, FaUserPlus, FaFileExcel } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const NewLeadPage: React.FC = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [mobile, setMobile] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !mobile.trim()) {
      setMessage('All fields are required.');
      setMessageType('error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email address.');
      setMessageType('error');
      return;
    }

    try {
      const payload = {
        firstName,
        lastName,
        email,
        mobile,
        source: 'MANUAL',
      };

      const authToken = localStorage.getItem('token');
      const response = await fetch('https://www.ayushvatika.com/api/admin/newlead/manual', {
      // const response = await fetch('http://localhost:8484/api/admin/newlead/manual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();

      if (response.ok) {
        setMessage('New lead added successfully!');
        setMessageType('success');
        setFirstName('');
        setLastName('');
        setEmail('');
        setMobile('');
      } else {
        setMessage(`Failed to add lead: ${responseText}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to connect to the server. Please check your network or server status.');
      setMessageType('error');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    } else {
      setSelectedFile(null);
    }
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!selectedFile) {
      setMessage('Please select an Excel file to upload.');
      setMessageType('error');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const authToken = localStorage.getItem('token');
      const response = await fetch('https://www.ayushvatika.com/api/admin/newlead/bulk', {
      // const response = await fetch('http://localhost:8484/api/admin/newlead/bulk', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData,
      });

      const responseText = await response.text();

      if (response.ok) {
        setMessage(responseText);
        setMessageType('success');
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('bulk-file-input') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        setMessage(`Failed to upload leads: ${responseText}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to connect to the server for bulk upload.');
      setMessageType('error');
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  // --- Styles for Dynamic Theme ---
  const inputClass = "w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors bg-white border-gray-300 text-gray-900 placeholder-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400";
  const labelClass = "block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2";

  return (
    <AdminLayout>
      <div className="container mx-auto p-8 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Manual Entry Section */}
          <div className="p-8 rounded-xl shadow-xl border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-3xl font-bold mb-6 text-center flex items-center justify-center text-gray-800 dark:text-white">
              <FaUserPlus className="mr-3 text-green-600 dark:text-green-400" /> Add New Lead
            </h2>
            
            {message && (
              <div className={`p-4 rounded-lg mb-6 text-white text-center font-semibold shadow-sm ${messageType === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
                {message}
              </div>
            )}
            
            <form onSubmit={handleManualSubmit}>
              <div className="space-y-6">
                <div>
                  <label className={labelClass}>First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={inputClass}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={inputClass}
                    placeholder="Enter last name"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Mobile Number</label>
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    className={inputClass}
                    placeholder="Enter mobile number"
                    required
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 mt-8">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 rounded-lg border font-semibold shadow-sm text-base transition-colors border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <FaTimesCircle className="mr-2" /> Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center justify-center font-semibold shadow-md text-base"
                >
                  <FaSave className="mr-2" /> Save Lead
                </button>
              </div>
            </form>
          </div>

          {/* Bulk Upload Section */}
          <div className="p-8 rounded-xl shadow-xl border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-3xl font-bold mb-6 text-center flex items-center justify-center text-gray-800 dark:text-white">
              <FaFileExcel className="mr-3 text-emerald-600 dark:text-emerald-400" /> Bulk Upload Leads
            </h2>
            <p className="mb-6 text-center text-gray-600 dark:text-gray-400 font-medium">
              Upload an Excel file (.xlsx or .xls) with columns: <br/>
              <span className="font-bold text-gray-800 dark:text-white">First Name, Last Name, Email, Mobile</span>.
            </p>
            
            <form onSubmit={handleBulkSubmit}>
              <div className="space-y-6">
                <div>
                  <label htmlFor="bulk-file-input" className={labelClass}>Select Excel File</label>
                  <input
                    type="file"
                    id="bulk-file-input"
                    onChange={handleFileChange}
                    accept=".xlsx, .xls"
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer shadow-sm
                    bg-gray-50 border-gray-300 text-gray-900 
                    dark:bg-gray-700 dark:border-gray-600 dark:text-white
                    file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold 
                    file:bg-emerald-100 file:text-emerald-700 hover:file:bg-emerald-200
                    dark:file:bg-emerald-900/30 dark:file:text-emerald-400 dark:hover:file:bg-emerald-900/50"
                    required
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 mt-8">
                <button
                  type="submit"
                  className="px-6 py-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center justify-center font-semibold shadow-md text-base"
                >
                  <FaSave className="mr-2" /> Upload Leads
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </AdminLayout>
  );
};

export default NewLeadPage;